import { gql } from '@apollo/client';

export const GET_USERS = gql`
    query Users(
        $from: Int
        $size: Int
        $sortField: String
        $sortOrder: String
        $groupId: String
        $crmGroupId: String
        $teamId: String
        $hasPersonalSmartInboxConnection: SmartInboxConnectionStatus
    ) {
        users(
            From: $from
            size: $size
            sortField: $sortField
            sortOrder: $sortOrder
            groupId: $groupId
            crmGroupId: $crmGroupId
            teamId: $teamId
            hasPersonalSmartInboxConnection: $hasPersonalSmartInboxConnection
        ) {
            users {
                id
                hasMfaDevice
                email
                firstName
                lastName
                groups {
                    id
                    name
                }
                crmGroups {
                    id
                    name
                }
                teams {
                    id
                    name
                    isDefault
                }
            }
            totalCount
        }
    }
`;

export const GET_USER_EMAIL_ADDRESSES = gql`
    query Users($from: Int, $size: Int, $groupId: String, $teamId: String) {
        users(From: $from, size: $size, groupId: $groupId, teamId: $teamId) {
            users {
                email
            }
        }
    }
`;

export const GET_USER = gql`
    query User($id: String!) {
        user(id: $id) {
            id
            email
            emailAliases
            firstName
            lastName
            sendEmailNotification
            language
            showExplanationsByDefault
            groups {
                id
                name
            }
            crmGroups {
                id
                name
            }
            teams {
                id
                name
            }
        }
    }
`;

export const RESET_USER_MFA = gql`
    mutation ResetUserMfa($userId: ID!) {
        resetUserMfa(userId: $userId) {
            success
            user {
                id
                hasMfaDevice
            }
        }
    }
`;

export const INVITE_USER = gql`
    mutation InviteUser(
        $email: String!
        $password1: String
        $password2: String
        $firstName: String!
        $lastName: String!
        $language: String
        $groups: [String]
        $crmGroups: [String]
        $teams: [String]
        $emailAliases: [String]
    ) {
        inviteUser(
            email: $email
            password1: $password1
            password2: $password2
            firstName: $firstName
            lastName: $lastName
            language: $language
            groups: $groups
            crmGroups: $crmGroups
            teams: $teams
            emailAliases: $emailAliases
        ) {
            success
        }
    }
`;

export const UPDATE_USER = gql`
    mutation UpdateUser(
        $id: String!
        $email: String!
        $firstName: String!
        $lastName: String!
        $language: String!
        $groups: [String]
        $crmGroups: [String]
        $teams: [String]
        $emailAliases: [String]
    ) {
        updateUser(
            id: $id
            email: $email
            firstName: $firstName
            lastName: $lastName
            language: $language
            groups: $groups
            crmGroups: $crmGroups
            teams: $teams
            emailAliases: $emailAliases
        ) {
            success
        }
    }
`;

export const DELETE_USER = gql`
    mutation DeleteUser($id: String!) {
        deleteUser(id: $id) {
            success
        }
    }
`;

export const UPDATE_PROFILE = gql`
    mutation UpdateUserProfile(
        $id: String!
        $sendEmailNotification: Boolean
        $firstName: String
        $lastName: String
        $language: String
        $showExplanationsByDefault: Boolean
    ) {
        updateUserProfile(
            id: $id
            sendEmailNotification: $sendEmailNotification
            firstName: $firstName
            lastName: $lastName
            language: $language
            showExplanationsByDefault: $showExplanationsByDefault
        ) {
            success
        }
    }
`;

export const CHANGE_PASSWORD = gql`
    mutation ChangePassword($oldPassword: String!, $newPassword1: String!, $newPassword2: String!) {
        changePassword(oldPassword: $oldPassword, newPassword1: $newPassword1, newPassword2: $newPassword2) {
            success
        }
    }
`;

export const RESET_PASSWORD = gql`
    mutation ResetPassword($email: String!) {
        resetPassword(email: $email) {
            done
        }
    }
`;

export const GET_ROLES = gql`
    query {
        groups {
            id
            name
            rolePermissions
        }
        crmGroups {
            id
            name
        }
    }
`;

export const GET_TEAMS = gql`
    query {
        teams {
            id
            name
            description
            users {
                id
                email
                firstName
                lastName
            }
            isDefault
        }
    }
`;

export const GET_MFA_DEVICES = gql`
    query GetMfaDevices {
        mfaDevices {
            deviceId
            name
            deviceType
            extraData
            createdAt
            lastUsedAt
        }
    }
`;

export const GET_MFA_DEVICE_SETUP_DATA = gql`
    query GetMfaDeviceSetupData($deviceType: String!) {
        mfaDeviceSetupData(deviceType: $deviceType) {
            ... on TotpMfaDeviceSetupDataType {
                qrCodeUrl
                secretKey
            }

            ... on WebauthnMfaDeviceSetupDataType {
                challenge
                options
            }

            ... on StaticMfaDeviceSetupDataType {
                tokens
            }
        }
    }
`;

export const GET_MFA_DEVICE_AUTHENTICATION_DATA = gql`
    query GetMfaDeviceAuthenticationData($deviceType: String!) {
        mfaDeviceAuthenticationData(deviceType: $deviceType) {
            ... on WebauthnMfaDeviceAuthenticationDataType {
                challenge
                options
            }
        }
    }
`;

export const REGISTER_MFA_DEVICE = gql`
    mutation RegisterMfaDevice($userPassword: String!, $deviceType: String!, $name: String!, $payload: GenericScalar) {
        registerMfaDevice(userPassword: $userPassword, deviceType: $deviceType, name: $name, payload: $payload) {
            success
            device {
                deviceId
                name
                deviceType
            }
        }
    }
`;

export const UNREGISTER_MFA_DEVICE = gql`
    mutation UnregisterMfaDevice($userPassword: String!, $deviceId: String!) {
        unregisterMfaDevice(userPassword: $userPassword, deviceId: $deviceId) {
            success
        }
    }
`;

const PERSONAL_SMART_INBOX_INTEGRATION_BASE_FRAGMENT = gql`
    fragment PersonalSmartInboxIntegrationBaseFields on PersonalSmartInboxIntegrationType {
        id
        name
        mailInbox
        enabled
        connectionFailedAt
        manualProcessingKeywords
        manualProcessingSenderEmails
        orderChannel {
            id
            name
        }
        rfqChannel {
            id
            name
        }
        createdBy {
            id
            email
            firstName
            lastName
        }
        agent {
            id
            name
        }
    }
`;

export const PERSONAL_SMART_INBOX_INTEGRATION = gql`
    query PersonalSmartInboxIntegration {
        personalSmartInboxIntegration {
            ...PersonalSmartInboxIntegrationBaseFields
        }
    }
    ${PERSONAL_SMART_INBOX_INTEGRATION_BASE_FRAGMENT}
`;

export const PERSONAL_SMART_INBOX_INTEGRATIONS = gql`
    query PersonalSmartInboxIntegrations {
        personalSmartInboxIntegrations {
            ...PersonalSmartInboxIntegrationBaseFields
        }
    }
    ${PERSONAL_SMART_INBOX_INTEGRATION_BASE_FRAGMENT}
`;

export const CONNECT_PERSONAL_MAILBOX_AUTH = gql`
    mutation ConnectPersonalMailboxAuth {
        connectPersonalMailboxAuth {
            success
            redirectUrl
        }
    }
`;

export const SETUP_PERSONAL_MAILBOX = gql`
    mutation SetupPersonalMailbox(
        $channels: [SmartInboxProductConfigInput!]
        $enabled: Boolean
        $manualProcessingKeywords: [String!]
        $manualProcessingSenderEmails: [String!]
        $integrationId: UUID
    ) {
        setupPersonalMailbox(
            channels: $channels
            enabled: $enabled
            manualProcessingKeywords: $manualProcessingKeywords
            manualProcessingSenderEmails: $manualProcessingSenderEmails
            integrationId: $integrationId
        ) {
            success
        }
    }
`;

export const GET_ADMIN_CONSENT_URL = gql`
    mutation GetAdminConsentUrl($isPersonalMailbox: Boolean!) {
        getAdminConsentUrl(isPersonalMailbox: $isPersonalMailbox) {
            adminConsentUrl
        }
    }
`;
